<template>
    <Panel
        id="campaign_summary"
        :title="$t(decommissioning ? 'Campaign Decommissioning' : 'Campaign Summary')"
        :page="page"
        :total-rows="items.length"
        @change="changePage"
    >
        <table id="campaigns_table" class="cic-table">
            <thead>
                <tr>
                    <th class="th-image" style="width: 16px">&nbsp;</th>
                    <th
                        v-for="column in Object.keys(columns)"
                        :class="{
                            'arrow-down': order.column === column && order.sort === 'asc',
                            'arrow-up': order.column === column && order.sort === 'desc',
                        }"
                        class="arrow"
                        @click="handleOrder(column)"
                    >
                        {{ $t(columns[column]) }}
                    </th>
                    <th>{{ $t('Access Number') }}</th>
                    <th v-if="decommissioning" style="width: 32px"></th>
                    <template v-else>
                        <th v-if="calculator" style="width: 32px"></th>
                        <th v-if="churn" style="width: 32px"></th>
                        <th v-if="$store.hasRight('bPauseCampaign')" style="width: 32px"></th>
                    </template>
                </tr>
            </thead>
            <tbody>
                <tr v-for="campaign in campaigns" @click="(evt) => handleOpen(campaign, evt)">
                    <td><span :class="getStatus(campaign)">&nbsp;</span></td>
                    <td class="primary">{{ campaign.name }}</td>
                    <td>{{ campaign.window.start.format('YYYY-MM-DD HH:mm') }}</td>
                    <td>{{ campaign.window.end.format('YYYY-MM-DD HH:mm') }}</td>
                    <td>{{ campaign.ioType === 0 ? $t('Outbound') : $t('Inbound') }}</td>
                    <td v-if="campaign.ioType === 0">{{ $t('N/A') }}</td>
                    <td v-else>
                        <ul>
                            <li v-for="number in campaign.numbers">
                                {{ number }}
                            </li>
                        </ul>
                    </td>
                    <td v-if="decommissioning">
                        <span class="stop-button" :title="$t('Decommission campaign')" @click="stop(campaign.scriptId)">
                            &nbsp;
                        </span>
                    </td>
                    <template v-else>
                        <td v-if="calculator" @click="calculate(campaign.scriptId)">
                            <span
                                v-if="campaign.ioType === 0"
                                class="calculate-button"
                                :title="$t('Recalculate Row Statuses')"
                            ></span>
                        </td>
                        <td v-if="churn" @click="refresh(campaign.scriptId)">
                            <span
                                v-if="campaign.ioType === 0"
                                class="refresh-button"
                                :title="$t('Churn campaign')"
                            ></span>
                        </td>
                        <td v-if="$store.hasRight('bPauseCampaign')">
                            <span
                                v-if="[0, 3].includes(campaign.status)"
                                class="pause-button"
                                :title="$t('Pause campaign')"
                                @click="pause(campaign.scriptId)"
                            >
                                &nbsp;
                            </span>
                            <span
                                v-else
                                class="play-button"
                                :title="$t('Resume campaign')"
                                @click="start(campaign.scriptId)"
                            >
                                &nbsp;
                            </span>
                        </td>
                    </template>
                </tr>
            </tbody>
        </table>
    </Panel>
</template>

<script>
    import moment from 'moment';
    import { load, save } from '@/utils/Http';
    import Panel from '@/components/container/Panel';
    import './resources/styles/campaigns.scss';

    export default {
        name: 'Campaigns',
        components: {
            Panel,
        },
        props: {
            decommissioning: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                calculator: false,
                churn: false,
                columns: {
                    name: 'Campaign',
                    start: 'Start',
                    end: 'End',
                    ioType: 'Type',
                },
                items: [],
                now: null,
                order: { column: 'name', sort: 'desc' },
                page: 1,
            };
        },
        computed: {
            campaigns() {
                return this.items.slice((this.page - 1) * 20, this.page * 20);
            },
        },
        created() {
            this.loadTable(true);
            this.now = moment();
        },
        methods: {
            calculate(scriptId) {
                save('campaign/recalculaterowstatus', { scriptId });
            },

            changePage(page) {
                this.page = page;
            },

            getStatus(campaign) {
                if (
                    ((campaign.ioType === 1 && campaign.numbers.length > 0) ||
                        (campaign.ioType === 0 && campaign.alertStatus !== 2)) &&
                    campaign.window.start <= this.now &&
                    campaign.window.end >= this.now
                ) {
                    return 'active';
                } else if (campaign.ioType === 0 && campaign.window.start > this.now) {
                    return 'pending';
                }

                return 'inactive';
            },

            handleOpen(campaign, evt) {
                if (
                    !this.decommissioning &&
                    campaign.ioType === 0 &&
                    (evt.target.classList.length === 0 ||
                        !['calculate-button', 'pause-button', 'play-button', 'refresh-button'].some((x) =>
                            evt.target.classList.contains(x),
                        ))
                ) {
                    this.$router.push('/campaign/' + campaign.scriptId.toString());
                }
            },

            handleOrder(column, first = false) {
                this.page = 1;
                if (this.order.column === column && !first) {
                    this.order = { column: this.order.column, sort: this.order.sort === 'asc' ? 'desc' : 'asc' };
                } else {
                    this.order = { column: column, sort: 'asc' };
                }

                this.items.sort((a, b) => {
                    let val1 = a[this.order.column];
                    let val2 = b[this.order.column];
                    if (this.order.column === 'ioType') {
                        val1 = a.ioType === 0 ? this.$t('Outbound') : this.$t('Inbound');
                        val2 = b.ioType === 0 ? this.$t('Outbound') : this.$t('Inbound');
                    } else if (['start', 'end'].includes(this.order.column)) {
                        val1 = a.window[this.order.column].format('YYYY-MM-DD HH:mm');
                        val2 = b.window[this.order.column].format('YYYY-MM-DD HH:mm');
                    }

                    return (this.order.sort === 'asc' && val1 > val2) || (this.order.sort === 'desc' && val2 > val1)
                        ? 1
                        : -1;
                });
            },

            loadTable(sort = false) {
                load('campaigns/get', { decommissioning: this.decommissioning ? 1 : 0 }, (response) => {
                    this.items = response.campaigns.map((x) => ({
                        ...x,
                        window: { start: moment(x.window.start), end: moment(x.window.end) },
                    }));
                    this.churn = response.churn;
                    this.calculator = response.calculator;

                    if (sort) {
                        this.handleOrder('name', true);
                    }
                });
            },

            pause(scriptId) {
                save('campaign/useschedule', { scriptId }, (response) => {
                    const resume = response.use && confirm(this.$t('CONFIRM_AUTO_RESUME_ON_NEXT_WINDOW'));
                    save('campaign/pause', { scriptId, resume: resume ? 1 : 0 }, () => {
                        this.loadTable();
                    });
                });
            },

            refresh(scriptId) {
                save('campaign/churn', { scriptId });
            },

            start(scriptId) {
                save('campaign/start', { scriptId }, () => {
                    this.loadTable();
                });
            },

            stop(scriptId) {
                save('campaign/decommission', { scriptId }, () => {
                    this.loadTable();
                });
            },
        },
    };
</script>
